import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaFacebook,
  FaInstagram,
  FaWhatsapp,
  FaLinkedin,
  FaDiscord,
  FaTimes,
  FaArrowLeft,
} from "react-icons/fa";
import { SiGmail } from "react-icons/si";
import { BsMicrosoftTeams } from "react-icons/bs";
import toast from "react-hot-toast";

interface ServicesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const steps = [
  {
    title: "What kind of business do you run?",
    options: [
      "Retail & Storefront",
      "Capstone Project",
      "Agency & Consulting",
      "Tech Startup / SaaS",
      "E-Commerce Brand",
      "Service Provider",
      "Other...",
    ],
    key: "businessType",
  },
  {
    title: "What do you want to make?",
    options: [
      "Custom Web App / SaaS",
      "Mobile Application",
      "Video Editing, Vlogs & Reels",
      "Business / E-Commerce Website",
      "POS / CRM / Internal Software",
    ],
    key: "projectType",
  },
  {
    title: "How big is your team?",
    options: [
      "Just Me (Solo Founder)",
      "2 - 5 People",
      "6 - 20 People",
      "20+ People",
      "🎓 Student",
    ],
    key: "teamSize",
  },
  {
    title: "What's your estimated budget?",
    options: ["Under $500", "$500 - $1,500", "$1,500 - $5,000", "$5,000+"],
    key: "budget",
  },
  {
    title: "Preferred communication?",
    options: [
      "Gmail",
      "Facebook",
      "Microsoft Teams",
      "WhatsApp",
      "LinkedIn",
      "Discord",
    ],
    key: "communication",
  },
];

const platformData: Record<
  string,
  { icon: React.ReactNode; hint: string; placeholder: string }
> = {
  Gmail: {
    icon: <SiGmail />,
    hint: "What's your email address?",
    placeholder: "hello@example.com",
  },
  Facebook: {
    icon: <FaFacebook />,
    hint: "Drop your profile link",
    placeholder: "facebook.com/yourname",
  },
  "Microsoft Teams": {
    icon: <BsMicrosoftTeams />,
    hint: "What's your Teams email/link?",
    placeholder: "email@domain.com",
  },
  WhatsApp: {
    icon: <FaWhatsapp />,
    hint: "Enter your phone number with country code",
    placeholder: "+63 912 345 6789",
  },
  LinkedIn: {
    icon: <FaLinkedin />,
    hint: "Drop your LinkedIn profile URL",
    placeholder: "linkedin.com/in/yourname",
  },
  Discord: {
    icon: <FaDiscord />,
    hint: "What's your Discord username?",
    placeholder: "@username",
  },
};

const ServicesModal: React.FC<ServicesModalProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [contactHandle, setContactHandle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false); // Added submission state

  if (!isOpen) return null;

  const currentStepData = steps[currentStep];
  if (!currentStepData) return null;

  const handleSelect = (option: string) => {
    setFormData((prev) => ({ ...prev, [currentStepData.key]: option }));
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    // Spam Prevention: 60-second cooldown
    const lastSubmit = localStorage.getItem("portfolio_last_submit");
    if (lastSubmit && Date.now() - parseInt(lastSubmit) < 60000) {
      toast.error("Please wait a minute before submitting another request.");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      formType: "Full Service Inquiry",
      platform: formData.communication,
      contactHandle: contactHandle,
      businessType: formData.businessType,
      projectType: formData.projectType,
      teamSize: formData.teamSize,
      budget: formData.budget,
    };

    try {
      const SHEET_API_URL = process.env.BUN_PUBLIC_SHEET_API_URL;

      if (!SHEET_API_URL) {
        throw new Error("API URL is not configured.");
      }

      await fetch(SHEET_API_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "text/plain",
        },
        body: JSON.stringify(payload),
      });

      toast.success("Service request sent! I'll review this and reach out.");

      // Record submission time for spam prevention
      localStorage.setItem("portfolio_last_submit", Date.now().toString());

      onClose();
      setTimeout(() => {
        setCurrentStep(0);
        setFormData({});
        setContactHandle("");
      }, 300);
    } catch (error) {
      console.error("Failed to send inquiry:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-lg bg-base-100 border border-base-300 rounded-3xl shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b border-base-200">
          <div className="flex items-center gap-3">
            {currentStep > 0 && (
              <button
                onClick={handleBack}
                className="text-base-content/60 hover:text-primary transition-colors"
              >
                <FaArrowLeft size={18} />
              </button>
            )}
            <div className="text-sm font-semibold text-primary">
              Step {currentStep + 1} of {steps.length}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-base-content/60 hover:text-error transition-colors rounded-full hover:bg-base-200"
          >
            <FaTimes size={20} />
          </button>
        </div>

        <div className="w-full h-1 bg-base-200">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: 0 }}
            animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        <div className="p-6 min-h-[350px] flex flex-col justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col gap-4"
            >
              <h2 className="text-2xl font-bold text-base-content mb-4 text-center">
                {currentStepData.title}
              </h2>

              {currentStep < steps.length - 1 ? (
                <div className="grid gap-3">
                  {currentStepData.options.map((option) => {
                    const isSelected = formData[currentStepData.key] === option;
                    const isStudent = option === "🎓 Student";

                    return (
                      <button
                        key={option}
                        onClick={() => handleSelect(option)}
                        className={`p-4 text-left rounded-xl border transition-all duration-200 flex items-center justify-between
                          ${
                            isSelected
                              ? "border-primary bg-primary/10 text-primary font-medium shadow-sm"
                              : isStudent
                                ? "border-primary/50 text-primary hover:bg-primary/5"
                                : "border-base-300 text-base-content/80 hover:border-primary/50 hover:bg-base-200"
                          }
                        `}
                      >
                        {option}
                        {isStudent && (
                          <span className="text-xs font-bold bg-primary/20 text-primary px-2 py-1 rounded-md">
                            Discount Eligible
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap justify-center gap-3">
                    {currentStepData.options.map((platform) => {
                      const isSelected =
                        formData[currentStepData.key] === platform;
                      return (
                        <button
                          key={platform}
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              [currentStepData.key]: platform,
                            }))
                          }
                          className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-all ${
                            isSelected
                              ? "border-primary bg-primary text-white shadow-md"
                              : "border-base-300 text-base-content/70 hover:bg-base-200"
                          }`}
                        >
                          {platformData[platform]?.icon}
                          <span className="font-medium text-sm text-gray-200">
                            {platform}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {(() => {
                    const selectedPlatform = formData[currentStepData.key];
                    const platformConfig = selectedPlatform
                      ? platformData[selectedPlatform]
                      : null;

                    return platformConfig ? (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-col gap-2 mt-4"
                      >
                        <label className="text-sm font-medium text-base-content/80">
                          {platformConfig.hint}
                        </label>
                        <input
                          type="text"
                          value={contactHandle}
                          onChange={(e) => setContactHandle(e.target.value)}
                          placeholder={platformConfig.placeholder}
                          className="w-full px-4 py-3 bg-base-200 border border-base-300 rounded-xl focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-gray-100 placeholder:text-gray-400"
                        />
                        <button
                          onClick={handleSubmit}
                          disabled={!contactHandle.trim() || isSubmitting}
                          className="mt-4 w-full py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20"
                        >
                          {isSubmitting ? "Sending..." : "Complete Request"}
                        </button>
                      </motion.div>
                    ) : null;
                  })()}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default ServicesModal;
